/* ===== Landing: illustrative product views ===== */

// Built with markup instead of screenshots so they follow the light/dark theme and stay light.
// Generic shapes and labels only: nothing here may look like real member or financial data.

import type { ReactNode } from 'react'
import { CalendarDays, ChevronLeft, ChevronRight, CreditCard, DollarSign, Megaphone, ShieldCheck, Users } from 'lucide-react'

function Line({ className = '' }: { readonly className?: string }) {
  return <span className={`block h-2 rounded-full bg-neutral-200 dark:bg-neutral-700 ${className}`} />
}

const CHIP_TONES = {
  success: 'bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-200',
  warning: 'bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-100',
  neutral: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300',
} as const

function Chip({ label, tone }: { readonly label: string; readonly tone: keyof typeof CHIP_TONES }) {
  return <span className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-semibold ${CHIP_TONES[tone]}`}>{label}</span>
}

function AppWindow({
  title,
  icon,
  action,
  children,
}: {
  readonly title: string
  readonly icon: ReactNode
  readonly action?: ReactNode
  readonly children: ReactNode
}) {
  return (
    <div aria-hidden="true" className="rounded-[22px] border border-neutral-200 bg-white p-4 shadow-card-elevated dark:border-neutral-800 dark:bg-neutral-900">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="grid h-8 w-8 place-items-center rounded-[10px] bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-300">
            {icon}
          </span>
          <span className="text-sm font-bold text-neutral-900 dark:text-neutral-100">{title}</span>
        </div>
        {action}
      </div>
      {children}
    </div>
  )
}

export function HeroIllustration() {
  return (
    <div aria-hidden="true" className="rounded-[22px] border border-neutral-200 bg-white p-3 shadow-card-elevated dark:border-neutral-800 dark:bg-neutral-900">
      <div className="flex items-center gap-1.5 px-2 pb-3">
        <span className="h-2.5 w-2.5 rounded-full bg-neutral-200 dark:bg-neutral-700" />
        <span className="h-2.5 w-2.5 rounded-full bg-neutral-200 dark:bg-neutral-700" />
        <span className="h-2.5 w-2.5 rounded-full bg-neutral-200 dark:bg-neutral-700" />
      </div>
      <div className="grid grid-cols-[108px_1fr] gap-3 rounded-[16px] bg-neutral-50 p-3 sm:grid-cols-[132px_1fr] dark:bg-neutral-950/60">
        <div className="space-y-1 rounded-[12px] bg-white p-2 dark:bg-neutral-900">
          <div className="mb-3 flex items-center gap-2 px-1">
            <span className="grid h-6 w-6 place-items-center rounded-[8px] bg-gradient-to-br from-primary-500 to-[#8e67ff] text-[10px] font-extrabold text-white">
              Z
            </span>
            <Line className="w-12" />
          </div>
          {['Inicio', 'Comunicados', 'Cuotas', 'Actividades', 'Finanzas', 'Chat'].map((label, index) => (
            <div
              key={label}
              className={`rounded-[8px] px-2 py-1.5 text-[10px] font-semibold ${
                index === 2 ? 'bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-200' : 'text-neutral-500 dark:text-neutral-400'
              }`}
            >
              {label}
            </div>
          ))}
        </div>
        <div className="min-w-0 space-y-3">
          <div className="rounded-[12px] bg-white p-3 dark:bg-neutral-900">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400">Cuotas del mes</p>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
              <div className="h-full w-2/3 rounded-full bg-primary-500" />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {['Al día', 'Pendientes', 'Exentos'].map((label) => (
                <div key={label} className="rounded-[8px] bg-neutral-50 p-2 dark:bg-neutral-800/60">
                  <span className="block h-2.5 w-6 rounded bg-neutral-300 dark:bg-neutral-600" />
                  <span className="mt-1.5 block truncate text-[9px] text-neutral-500 dark:text-neutral-400">{label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-[12px] bg-white p-3 dark:bg-neutral-900">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400">Próximas actividades</p>
            {[0, 1, 2].map((row) => (
              <div key={row} className="mt-2 flex items-center gap-2">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-[8px] bg-primary-50 dark:bg-primary-500/15">
                  <span className="h-2 w-3 rounded-sm bg-primary-400" />
                </span>
                <span className="flex-1 space-y-1">
                  <Line className="w-3/4" />
                  <span className="block h-2 w-1/2 rounded-full bg-neutral-100 dark:bg-neutral-800" />
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export function DuesIllustration() {
  const roster: { tone: keyof typeof CHIP_TONES; label: string }[] = [
    { tone: 'success', label: 'Pagada' },
    { tone: 'warning', label: 'Pendiente' },
    { tone: 'success', label: 'Pagada' },
    { tone: 'neutral', label: 'Exenta' },
    { tone: 'success', label: 'Pagada' },
  ]

  return (
    <div aria-hidden="true" className="mx-auto flex max-w-md items-end justify-center gap-4">
      <div className="mb-8 hidden w-52 rounded-[20px] border border-neutral-200 bg-white p-3.5 shadow-card-elevated sm:block dark:border-neutral-800 dark:bg-neutral-900">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-bold text-neutral-900 dark:text-neutral-100">Nómina del mes</p>
          <Users className="h-3.5 w-3.5 text-neutral-400" />
        </div>
        {roster.map((row, index) => (
          <div key={index} className="mt-2.5 flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <span className="h-6 w-6 rounded-full bg-neutral-200 dark:bg-neutral-700" />
              <Line className="w-16" />
            </span>
            <Chip label={row.label} tone={row.tone} />
          </div>
        ))}
      </div>

      <div className="w-56 shrink-0 rounded-[34px] border-[6px] border-neutral-900 bg-neutral-50 p-2.5 shadow-card-elevated dark:border-neutral-700 dark:bg-neutral-950">
        <div className="mx-auto mb-2.5 h-1.5 w-14 rounded-full bg-neutral-300 dark:bg-neutral-700" />
        <div className="rounded-[18px] bg-white p-3 dark:bg-neutral-900">
          <div className="flex items-start justify-between gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-[10px] bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-300">
              <CreditCard className="h-4 w-4" />
            </span>
            <Chip label="Pendiente" tone="warning" />
          </div>
          <p className="mt-2 text-[12px] font-bold text-neutral-900 dark:text-neutral-100">Mi cuota del mes</p>
          <Line className="mt-1.5 w-24" />
          <span className="mt-3 block rounded-[11px] bg-primary-500 py-2 text-center text-[11px] font-semibold text-white">Pagar en línea</span>
          <span className="mt-2 block rounded-[11px] border border-neutral-200 py-2 text-center text-[11px] font-semibold text-neutral-600 dark:border-neutral-700 dark:text-neutral-300">
            Datos para transferencia
          </span>
        </div>
        <div className="mt-2.5 rounded-[18px] bg-white p-3 dark:bg-neutral-900">
          <p className="text-[11px] font-bold text-neutral-900 dark:text-neutral-100">Mi historial</p>
          {[0, 1, 2].map((row) => (
            <div key={row} className="mt-2 flex items-center justify-between gap-2">
              <Line className="w-20" />
              <Chip label="Pagada" tone="success" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function AnnouncementsIllustration() {
  return (
    <AppWindow
      title="Comunicados"
      icon={<Megaphone className="h-4 w-4" />}
      action={<span className="rounded-[9px] bg-primary-500 px-2.5 py-1 text-[11px] font-semibold text-white">Publicar</span>}
    >
      <div className="space-y-3">
        <div className="rounded-[14px] border border-primary-200 bg-primary-50/60 p-3 dark:border-primary-500/30 dark:bg-primary-500/10">
          <div className="flex items-center justify-between gap-3">
            <span className="block h-2.5 w-2/3 rounded-full bg-primary-300 dark:bg-primary-500/60" />
            <Line className="w-10" />
          </div>
          <Line className="mt-3 w-full" />
          <Line className="mt-1.5 w-11/12" />
          <Line className="mt-1.5 w-3/5" />
          <div className="mt-3 flex items-center">
            {[0, 1, 2, 3].map((avatar) => (
              <span
                key={avatar}
                className="-ml-1.5 h-5 w-5 rounded-full border-2 border-white bg-neutral-300 first:ml-0 dark:border-neutral-900 dark:bg-neutral-600"
              />
            ))}
            <Line className="ml-2 w-14" />
          </div>
        </div>
        {[0, 1].map((card) => (
          <div key={card} className="rounded-[14px] border border-neutral-200 p-3 dark:border-neutral-800">
            <div className="flex items-center justify-between gap-3">
              <span className="block h-2.5 w-1/2 rounded-full bg-neutral-300 dark:bg-neutral-600" />
              <Line className="w-10" />
            </div>
            <Line className="mt-3 w-full" />
            <Line className="mt-1.5 w-2/3" />
          </div>
        ))}
      </div>
    </AppWindow>
  )
}

const WEEKDAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D']
const HIGHLIGHTED_DAYS = [9, 16, 24]

export function EventsIllustration() {
  return (
    <AppWindow
      title="Actividades"
      icon={<CalendarDays className="h-4 w-4" />}
      action={
        <span className="flex items-center gap-1 text-neutral-400">
          <ChevronLeft className="h-4 w-4" />
          <Line className="w-14" />
          <ChevronRight className="h-4 w-4" />
        </span>
      }
    >
      <div className="grid grid-cols-7 gap-1 text-center text-[9px] font-semibold text-neutral-400">
        {WEEKDAYS.map((day, index) => (
          <span key={index}>{day}</span>
        ))}
      </div>
      <div className="mt-1.5 grid grid-cols-7 gap-1">
        {Array.from({ length: 35 }, (_, index) => {
          const day = index - 1
          const inMonth = day >= 1 && day <= 30
          const highlighted = HIGHLIGHTED_DAYS.includes(day)
          return (
            <span
              key={index}
              className={`flex h-7 items-center justify-center rounded-[7px] text-[10px] ${
                !inMonth
                  ? 'text-transparent'
                  : highlighted
                    ? 'bg-primary-500 font-semibold text-white'
                    : 'bg-neutral-50 text-neutral-600 dark:bg-neutral-800/60 dark:text-neutral-300'
              }`}
            >
              {inMonth ? day : '.'}
            </span>
          )
        })}
      </div>
      <div className="mt-4 flex items-center gap-3 rounded-[14px] border border-neutral-200 p-3 dark:border-neutral-800">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-300">
          <CalendarDays className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <span className="block h-2.5 w-3/4 rounded-full bg-neutral-300 dark:bg-neutral-600" />
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
            <div className="h-full w-3/5 rounded-full bg-success-500" />
          </div>
          <span className="mt-1.5 flex items-center gap-1 text-[10px] text-neutral-500 dark:text-neutral-400">
            <Users className="h-3 w-3" /> Cupos
          </span>
        </div>
        <span className="shrink-0 rounded-[9px] bg-primary-500 px-2.5 py-1 text-[11px] font-semibold text-white">Inscribirme</span>
      </div>
    </AppWindow>
  )
}

const FINANCE_BARS: readonly (readonly [number, number])[] = [
  [55, 40],
  [70, 48],
  [62, 35],
  [84, 52],
  [76, 58],
  [92, 45],
]

export function FinanceIllustration() {
  return (
    <AppWindow
      title="Finanzas"
      icon={<DollarSign className="h-4 w-4" />}
      action={<Line className="w-16" />}
    >
      <div className="grid grid-cols-3 gap-2">
        {['Ingresos', 'Egresos', 'Balance'].map((label) => (
          <div key={label} className="rounded-[12px] bg-neutral-50 p-2.5 dark:bg-neutral-800/60">
            <span className="block text-[10px] text-neutral-500 dark:text-neutral-400">{label}</span>
            <span className="mt-2 block h-2.5 w-14 rounded-full bg-neutral-300 dark:bg-neutral-600" />
          </div>
        ))}
      </div>
      <div className="mt-4 flex h-32 items-end gap-3 border-b border-neutral-200 px-1 dark:border-neutral-800">
        {FINANCE_BARS.map(([income, expense], index) => (
          <div key={index} className="flex h-full flex-1 items-end justify-center gap-1">
            <span className="w-2.5 rounded-t-[4px] bg-primary-500" style={{ height: `${income}%` }} />
            <span className="w-2.5 rounded-t-[4px] bg-neutral-300 dark:bg-neutral-600" style={{ height: `${expense}%` }} />
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-4 text-[10px] text-neutral-500 dark:text-neutral-400">
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-primary-500" /> Ingresos
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-neutral-300 dark:bg-neutral-600" /> Egresos
        </span>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3 rounded-[12px] border border-warning-200 bg-warning-50 px-3 py-2 dark:border-warning-500/30 dark:bg-warning-500/10">
        <span className="flex items-center gap-2 text-[11px] font-semibold text-warning-700 dark:text-warning-100">
          <ShieldCheck className="h-3.5 w-3.5" /> Egreso por aprobar
        </span>
        <span className="rounded-[8px] bg-white px-2 py-0.5 text-[10px] font-semibold text-neutral-700 dark:bg-neutral-900 dark:text-neutral-200">
          Revisar
        </span>
      </div>
    </AppWindow>
  )
}
